import { useEffect, useRef, useCallback, useState } from 'react';
import './V75Vignett.css';

/**
 * V75Vignett - Action-preget intro/uttro for "V75 TIPS".
 *
 * Design:
 *  - Full-screen race-footage bakgrunn (mørkgradert, høy-kontrast)
 *  - Diagonale speed-streaks som sveiper
 *  - Røde chevron-striper topp/bunn
 *  - Pulserende røde lys-søyler i sidene
 *  - V75 chrome-logo som SLÅR inn med camera shake + hvit flash
 *  - "TIPS" chrome-tekst som slam-lander med rødt/hvit shimmer
 *  - Iris-wipe til grønn (kun variant="in")
 *
 * Varianter:
 *  - "in":  komplett sekvens + iris-wipe → grønn chromakey
 *  - "out": samme bygg-opp, INGEN wipe, siste bilde blir stående
 *
 * State: show_v75_intro (variant="in") / show_v75_outro (variant="out")
 */
export function V75Vignett({ show, variant = 'in' }) {
  const audioRef = useRef(null);
  const bgVideoRef = useRef(null);
  const fadeIntervalRef = useRef(null);
  const timersRef = useRef([]);
  const [visualsVisible, setVisualsVisible] = useState(false);
  const [wipeActive, setWipeActive] = useState(false);
  const [greenHold, setGreenHold] = useState(false);

  // "in" timeline
  const WIPE_AT    = 8600;   // etter alt er ferdig og har pulsed litt
  const HIDE_AT    = 9900;
  const FADE_START = 10100;
  const FADE_DUR   = 2000;
  const FINISH_AT  = 12500;
  const START_VOL  = 0.85;

  const clearAllTimers = useCallback(() => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
    if (fadeIntervalRef.current) {
      clearInterval(fadeIntervalRef.current);
      fadeIntervalRef.current = null;
    }
  }, []);

  const stopAndResetAudio = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = START_VOL;
      audio.muted = false;
    } catch (_) { /* noop */ }
  }, []);

  const stateKey = variant === 'out' ? 'show_v75_outro' : 'show_v75_intro';
  const finish = useCallback(() => {
    fetch('/api/overlay-state', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [stateKey]: false }),
    }).catch(() => { /* noop */ });
  }, [stateKey]);

  const attachBgVideo = (el) => {
    bgVideoRef.current = el;
    if (!el) return;
    try {
      el.muted = true;
      el.loop = true;
      el.playsInline = true;
      const p = el.play();
      if (p && p.catch) p.catch(() => { /* browser blocked */ });
    } catch (_) { /* noop */ }
  };

  useEffect(() => {
    if (!show) {
      clearAllTimers();
      setVisualsVisible(false);
      setWipeActive(false);
      setGreenHold(false);
      stopAndResetAudio();
      return;
    }

    clearAllTimers();
    stopAndResetAudio();
    setVisualsVisible(true);
    setWipeActive(false);
    setGreenHold(false);

    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = 0;
      audio.volume = START_VOL;
      audio.muted = false;
      const p = audio.play();
      if (p && p.catch) {
        p.catch(() => {
          audio.muted = true;
          audio.play().then(() => {
            setTimeout(() => { if (audioRef.current) audioRef.current.muted = false; }, 120);
          }).catch(() => { /* noop */ });
        });
      }
    }

    const add = (fn, ms) => { timersRef.current.push(setTimeout(fn, ms)); };

    const startFadeAt = (fadeStartMs) => {
      add(() => {
        const a = audioRef.current;
        if (!a) return;
        const startVol = a.volume || START_VOL;
        const steps = 40;
        const stepMs = FADE_DUR / steps;
        let i = 0;
        fadeIntervalRef.current = setInterval(() => {
          i += 1;
          const live = audioRef.current;
          if (!live) {
            if (fadeIntervalRef.current) { clearInterval(fadeIntervalRef.current); fadeIntervalRef.current = null; }
            return;
          }
          try { live.volume = Math.max(0, startVol * (1 - i / steps)); } catch (_) { /* noop */ }
          if (i >= steps) {
            if (fadeIntervalRef.current) { clearInterval(fadeIntervalRef.current); fadeIntervalRef.current = null; }
            try { live.pause(); live.currentTime = 0; live.volume = START_VOL; } catch (_) { /* noop */ }
          }
        }, stepMs);
      }, fadeStartMs);
    };

    if (variant === 'in') {
      add(() => setWipeActive(true), WIPE_AT);
      add(() => {
        setVisualsVisible(false);
        setGreenHold(true);
      }, HIDE_AT);
      startFadeAt(FADE_START);
      add(() => {
        setGreenHold(false);
        finish();
      }, FINISH_AT);
    } else {
      startFadeAt(FADE_START);
      // Scene blir stående til user setter show=false.
    }

    return () => { clearAllTimers(); };
  }, [show, variant, finish, clearAllTimers, stopAndResetAudio]);

  return (
    <>
      <audio
        ref={audioRef}
        src="/assets/v75-music.mp3"
        preload="auto"
        style={{ display: 'none' }}
        data-testid={variant === 'out' ? 'v75-outro-audio' : 'v75-intro-audio'}
      />

      {greenHold && (
        <div
          data-testid="v75-green-hold"
          style={{ position: 'absolute', inset: 0, backgroundColor: '#00FF00', zIndex: 300 }}
        />
      )}

      {visualsVisible && (
        <div className="v75-root" data-testid={variant === 'out' ? 'v75-outro-overlay' : 'v75-intro-overlay'}>
          {/* Camera shake wrapper — kicks in at logo slam */}
          <div className="v75-shake">
            {/* Fullscreen race footage backdrop */}
            <video
              className="v75-bg-video"
              ref={attachBgVideo}
              muted
              loop
              playsInline
              preload="auto"
            >
              <source src="/assets/trav3.webm" type="video/webm" />
              <source src="/assets/trav3.mp4" type="video/mp4" />
            </video>
            <div className="v75-bg-overlay" />

            {/* Diagonal speed streaks */}
            <div className="v75-streaks" />

            {/* Red vertical energy bars */}
            <div className="v75-side-bar v75-side-left" />
            <div className="v75-side-bar v75-side-right" />

            {/* Chevron strips top/bottom */}
            <div className="v75-strip v75-strip-top" />
            <div className="v75-strip v75-strip-bot" />

            {/* Full-screen flash on logo slam */}
            <div className="v75-flash" />

            {/* V75 3D-logo — flyr inn fra dypet, spinner og lander med dybde + bevel */}
            <div className="v75-logo-wrap" data-testid="v75-logo-3d">
              <div className="v75-logo-glow" />
              <div className="v75-logo-fly">
                <div className="v75-logo-3d">
                  {Array.from({ length: 14 }).map((_, i) => (
                    <img
                      key={i}
                      className="v75-logo-layer"
                      style={{ '--z': -(i + 1) }}
                      src="/assets/v75-logo-blue.png"
                      alt=""
                      aria-hidden="true"
                    />
                  ))}
                  <img className="v75-logo-face" src="/assets/v75-logo-blue.png" alt="V75" />
                  <div className="v75-logo-bevel-light" style={{ WebkitMaskImage: 'url(/assets/v75-logo-blue.png)', maskImage: 'url(/assets/v75-logo-blue.png)' }} />
                  <div className="v75-logo-bevel-dark" style={{ WebkitMaskImage: 'url(/assets/v75-logo-blue.png)', maskImage: 'url(/assets/v75-logo-blue.png)' }} />
                  <div className="v75-logo-shine3d" style={{ WebkitMaskImage: 'url(/assets/v75-logo-blue.png)', maskImage: 'url(/assets/v75-logo-blue.png)' }} />
                </div>
              </div>
            </div>

            {/* Spark particles that burst from logo center at slam */}
            <div className="v75-sparks">
              {Array.from({ length: 14 }).map((_, i) => (
                <div key={i} className={`v75-spark v75-spark-${i}`} />
              ))}
            </div>

            {/* TIPS text */}
            <div className="v75-tips" data-testid={variant === 'out' ? 'v75-outro-tips' : 'v75-intro-tips'}>
              {'TIPS'.split('').map((c, i) => (
                <span key={i} style={{ '--tips-delay': `${2.5 + i * 0.15}s` }}>{c}</span>
              ))}
            </div>

            {/* Red divider */}
            <div className="v75-divider" />

            {/* Subtitle */}
            <div className="v75-sub">Sørlandets Travpark</div>
          </div>

          {/* Iris wipe (variant="in" only) */}
          {variant === 'in' && (
            <div className={`v75-iris ${wipeActive ? 'active' : ''}`} data-testid="v75-iris" />
          )}
        </div>
      )}
    </>
  );
}
